import { useEffect, useRef } from "react";
import { trackFeedEngaged } from "../analytics/canonicalTracking";

const DEBOUNCE_MS = 750;

function mapVisionToFeedType(
  visionType: string
): "explore" | "following" | "recommended" {
  if (visionType === "Explore") return "explore";
  if (visionType === "My Subscription") return "following";
  return "recommended";
}

/**
 * Phase 2: feed_engaged — debounce скролу; чекпоінти 25/50/75/100% (не частіше 1 разу на чекпоінт за візит табу).
 */
export function useFeedEngagement(
  visionType: string,
  userId?: string,
  userRole?: string
): void {
  const checkpointsFired = useRef<Set<number>>(new Set());
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    checkpointsFired.current = new Set();
  }, [visionType]);

  useEffect(() => {
    const feed_type = mapVisionToFeedType(visionType);

    const computeDepth = () => {
      const doc = document.documentElement;
      const maxScroll = Math.max(
        1,
        doc.scrollHeight - window.innerHeight
      );
      return Math.min(100, Math.round((window.scrollY / maxScroll) * 100));
    };

    const onScroll = () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        const depth = computeDepth();
        const checkpoints = [25, 50, 75, 100];
        for (const c of checkpoints) {
          if (depth >= c && !checkpointsFired.current.has(c)) {
            checkpointsFired.current.add(c);
            trackFeedEngaged({
              feed_type,
              scroll_depth: c,
              items_seen: Math.min(100, Math.floor(c / 5)),
              user_id: userId,
              user_role: userRole,
            });
          }
        }
      }, DEBOUNCE_MS);
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [visionType, userId, userRole]);
}
